import type { PassportSchemaDef } from "../types.js";

/**
 * DRAFT pending the final ESPR textile delegated act (expected 2027).
 * Modeled on the JRC textile DPP content proposal structure (May 2026):
 * 49 data points in 4 categories. We do not have the official JRC field list —
 * this is a well-reasoned draft from research/02-teknik-standartlar.md and
 * standard textile DPP practice. Field ids are STABLE (persisted in passport
 * versions); the list will be trued up against the official JRC spec by adding
 * fields / flipping `required` and `tier`, never by renaming ids.
 *
 * Tier logic (EN 18239 layered access): consumer-relevant facts are public;
 * supply-chain detail (facilities, origin stages, declarations) is restricted
 * to authenticated roles (brand customer, recycler, authority).
 */
export const textileV1: PassportSchemaDef = {
  id: "textile-v1",
  version: "1.0.0-draft",
  title: {
    tr: "Tekstil Ürün Pasaportu",
    en: "Textile Product Passport",
    de: "Textil-Produktpass",
  },
  // Draft vocabulary: schema.org for generic product terms, GS1 web vocabulary
  // for identifiers, own namespace for ESPR-specific terms until the delegated
  // act fixes an official context.
  jsonLdContext: {
    "@version": 1.1,
    "@vocab": "https://vocab.idpp.net/textile/v1#",
    schema: "https://schema.org/",
    gs1: "https://gs1.org/voc/",
    identifier: "gs1:gtin",
    issuer: "schema:issuedBy",
    issuedAt: "schema:dateCreated",
  },
  categories: [
    {
      id: "identity",
      labels: { tr: "Ürün Kimliği", en: "Product Identity", de: "Produktidentität" },
      fields: [
        {
          id: "identity.productName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Ürün adı", en: "Product name", de: "Produktname" },
        },
        {
          id: "identity.productDescription",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Ürün özeti", en: "Product summary", de: "Produktbeschreibung" },
          description: "Short consumer-facing description of the product and its intended use.",
          presentation: { kind: "summary" },
        },
        {
          id: "identity.gtin",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "GTIN", en: "GTIN", de: "GTIN" },
          description: "GS1 Global Trade Item Number (8/12/13/14 digits).",
        },
        {
          id: "identity.productGroup",
          type: "enum",
          required: true,
          tier: "public",
          enumValues: ["apparel", "home-textile", "accessory", "fabric", "other"],
          labels: { tr: "Ürün grubu", en: "Product group", de: "Produktgruppe" },
        },
        {
          id: "identity.brand",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Marka", en: "Brand", de: "Marke" },
        },
        {
          id: "identity.model",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Model/stil referansı", en: "Model / style reference", de: "Modell-/Stilreferenz" },
        },
        {
          id: "identity.countryOfFinalManufacture",
          type: "country",
          required: true,
          tier: "public",
          labels: {
            tr: "Nihai üretim ülkesi",
            en: "Country of final manufacture",
            de: "Land der Endfertigung",
          },
          description: "ISO 3166-1 alpha-2 code (e.g. TR).",
          highlight: true,
        },
        {
          id: "identity.manufacturerName",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Üretici adı", en: "Manufacturer name", de: "Herstellername" },
        },
        {
          id: "identity.manufacturerEori",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Üretici EORI numarası",
            en: "Manufacturer EORI number",
            de: "EORI-Nummer des Herstellers",
          },
          description: "EU economic operator registration id, needed for customs/registry flows.",
        },
        {
          id: "identity.facilityId",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Nihai üretim tesisi kimliği",
            en: "Final manufacturing facility id",
            de: "Kennung der Endfertigungsstätte",
          },
          description: "Facility identifier such as a GS1 GLN.",
        },
        {
          id: "identity.batchNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Parti numarası", en: "Batch number", de: "Chargennummer" },
        },
        {
          id: "identity.commodityCode",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "Emtia kodu (GTİP/CN)", en: "Commodity code (CN/HS)", de: "Warennummer (KN/HS)" },
          description: "Needed for the EU Registry registration record.",
        },
      ],
    },
    {
      id: "materials",
      labels: {
        tr: "Malzeme Bileşimi ve Kimyasallar",
        en: "Material Composition & Chemicals",
        de: "Materialzusammensetzung & Chemikalien",
      },
      fields: [
        {
          id: "materials.fiberComposition",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Elyaf bileşimi", en: "Fiber composition", de: "Faserzusammensetzung" },
          description: 'Percentages by weight, e.g. "60% cotton / 40% recycled polyester".',
          highlight: true,
        },
        {
          id: "materials.compositionList",
          type: "group",
          required: false,
          tier: "public",
          labels: {
            tr: "Katman bazlı kompozisyon",
            en: "Composition by layer",
            de: "Zusammensetzung nach Lage",
          },
          description:
            "Structured, layer-level alternative to the free-text fiber composition (body/lining/trims).",
          itemFields: [
            {
              key: "layer",
              type: "enum",
              required: true,
              enumValues: ["body", "lining", "trim", "other"],
              labels: { tr: "Katman", en: "Layer", de: "Lage" },
            },
            {
              key: "material",
              type: "string",
              required: true,
              labels: { tr: "Malzeme", en: "Material", de: "Material" },
            },
            {
              key: "percentage",
              type: "percentage",
              required: true,
              unit: "%",
              labels: { tr: "Oran", en: "Share", de: "Anteil" },
            },
            {
              key: "recycled",
              type: "boolean",
              required: false,
              labels: { tr: "Geri dönüştürülmüş", en: "Recycled", de: "Recycelt" },
            },
            {
              key: "originCountry",
              type: "country",
              required: false,
              labels: { tr: "Menşe ülkesi", en: "Country of origin", de: "Ursprungsland" },
            },
          ],
        },
        {
          id: "materials.recycledContentPct",
          type: "percentage",
          required: true,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülmüş içerik",
            en: "Recycled content",
            de: "Rezyklatanteil",
          },
          highlight: true,
        },
        {
          id: "materials.recycledContentPostConsumerPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Tüketici sonrası geri dönüştürülmüş içerik",
            en: "Post-consumer recycled content",
            de: "Post-Consumer-Rezyklatanteil",
          },
        },
        {
          id: "materials.bioBasedContentPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: { tr: "Biyo-bazlı içerik", en: "Bio-based content", de: "Biobasierter Anteil" },
        },
        {
          id: "materials.animalOriginMaterials",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Hayvansal kaynaklı malzeme içerir",
            en: "Contains materials of animal origin",
            de: "Enthält Materialien tierischen Ursprungs",
          },
          description: "Leather, wool, down, silk etc.",
        },
        {
          id: "materials.trimsMaterials",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Aksesuar/garnitür malzemeleri",
            en: "Trims & accessories materials",
            de: "Materialien von Zutaten & Accessoires",
          },
          description: "Buttons, zippers, threads — relevant for recyclers.",
        },
        {
          id: "materials.fabricWeightGsm",
          type: "number",
          required: false,
          tier: "restricted",
          unit: "g/m²",
          labels: { tr: "Kumaş gramajı", en: "Fabric weight", de: "Flächengewicht" },
        },
        {
          id: "materials.spinningCountry",
          type: "country",
          required: false,
          tier: "restricted",
          labels: {
            tr: "İplik üretim ülkesi",
            en: "Spinning country",
            de: "Land der Garnherstellung",
          },
          description: "Origin stage 1 of 4 (ISO 3166-1 alpha-2).",
        },
        {
          id: "materials.fabricProductionCountry",
          type: "country",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Kumaş üretim ülkesi",
            en: "Fabric production country",
            de: "Land der Stoffherstellung",
          },
          description: "Origin stage 2 of 4 — weaving/knitting.",
        },
        {
          id: "materials.dyeingFinishingCountry",
          type: "country",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Boyama/terbiye ülkesi",
            en: "Dyeing & finishing country",
            de: "Land der Färbung/Ausrüstung",
          },
          description: "Origin stage 3 of 4.",
        },
        {
          id: "materials.assemblyCountry",
          type: "country",
          required: false,
          tier: "restricted",
          labels: { tr: "Konfeksiyon ülkesi", en: "Assembly country", de: "Land der Konfektion" },
          description: "Origin stage 4 of 4 — making-up/sewing.",
        },
        {
          id: "materials.svhcPresent",
          type: "boolean",
          required: true,
          tier: "public",
          labels: {
            tr: "SVHC içerir (>%0,1 a/a)",
            en: "Contains SVHC (>0.1% w/w)",
            de: "Enthält SVHC (>0,1 % w/w)",
          },
          description: "REACH candidate-list substances of very high concern above 0.1% by weight.",
        },
        {
          id: "materials.svhcSubstances",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "SVHC madde listesi",
            en: "SVHC substances",
            de: "SVHC-Stoffe",
          },
          description: "Substance names/CAS numbers — expected when svhcPresent is true.",
        },
        {
          id: "materials.scipNumber",
          deprecatedBy: "compliance.scipNumber",
          type: "string",
          required: false,
          tier: "restricted",
          labels: { tr: "SCIP kayıt numarası", en: "SCIP number", de: "SCIP-Nummer" },
          description: "ECHA SCIP database reference for articles containing SVHC.",
        },
        {
          id: "materials.reachDeclarationUrl",
          deprecatedBy: "compliance.reachDeclarationUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "REACH uygunluk beyanı",
            en: "REACH declaration of compliance",
            de: "REACH-Konformitätserklärung",
          },
        },
        {
          id: "materials.certifications",
          type: "string",
          required: false,
          tier: "public",
          labels: { tr: "Sertifikalar", en: "Certifications", de: "Zertifizierungen" },
          description: 'E.g. "OEKO-TEX Standard 100; GOTS; bluesign".',
        },
      ],
    },
    {
      id: "circularity",
      labels: {
        tr: "Dayanıklılık ve Döngüsellik",
        en: "Durability & Circularity",
        de: "Haltbarkeit & Kreislauffähigkeit",
      },
      fields: [
        {
          id: "circularity.expectedLifespanYears",
          type: "number",
          required: true,
          tier: "public",
          unit: "years",
          labels: {
            tr: "Beklenen kullanım ömrü",
            en: "Expected lifespan",
            de: "Erwartete Lebensdauer",
          },
          highlight: true,
        },
        {
          id: "circularity.durabilityTestStandard",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Dayanıklılık test standardı",
            en: "Durability test standard",
            de: "Haltbarkeits-Prüfnorm",
          },
          description: "E.g. Martindale abrasion, ISO 12945 pilling — evidence for the lifespan claim.",
        },
        {
          id: "circularity.repairabilityScore",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["A", "B", "C", "D", "E"],
          labels: { tr: "Onarılabilirlik sınıfı", en: "Repairability score", de: "Reparierbarkeitsklasse" },
        },
        {
          id: "circularity.repairInstructionsUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: {
            tr: "Onarım talimatları",
            en: "Repair instructions",
            de: "Reparaturanleitung",
          },
        },
        {
          id: "circularity.sparePartsAvailable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: {
            tr: "Yedek parça mevcut",
            en: "Spare parts available",
            de: "Ersatzteile verfügbar",
          },
          description: "Buttons, zippers, repair patches etc.",
        },
        {
          id: "circularity.sparePartsInfo",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yedek parça bilgisi",
            en: "Spare parts information",
            de: "Ersatzteilinformationen",
          },
        },
        {
          id: "circularity.recyclable",
          type: "boolean",
          required: true,
          tier: "public",
          labels: { tr: "Geri dönüştürülebilir", en: "Recyclable", de: "Recycelbar" },
          highlight: true,
        },
        {
          id: "circularity.recyclabilityPct",
          type: "percentage",
          required: false,
          tier: "public",
          unit: "%",
          labels: {
            tr: "Geri dönüştürülebilirlik oranı",
            en: "Recyclability rate",
            de: "Recyclingfähigkeitsquote",
          },
        },
        {
          id: "circularity.disassemblyInfo",
          type: "string",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Söküm/ayrıştırma bilgisi",
            en: "Disassembly information",
            de: "Demontageinformationen",
          },
          description: "For recyclers: how to separate trims, linings, multi-material parts.",
        },
        {
          id: "circularity.disassemblyInstructionsUrl",
          type: "url",
          required: false,
          tier: "restricted",
          labels: {
            tr: "Söküm talimatları bağlantısı",
            en: "Disassembly instructions link",
            de: "Link zur Demontageanleitung",
          },
        },
        {
          id: "circularity.microplasticSheddingClass",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["low", "medium", "high"],
          labels: {
            tr: "Mikroplastik salınım sınıfı",
            en: "Microplastic shedding class",
            de: "Mikroplastik-Freisetzungsklasse",
          },
          description: "Microfibre release during washing — classification method pending the delegated act.",
        },
        {
          id: "circularity.packagingRecyclability",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Ambalaj geri dönüştürülebilirliği",
            en: "Packaging recyclability",
            de: "Recyclingfähigkeit der Verpackung",
          },
        },
        {
          id: "circularity.narrative",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Sürdürülebilirlik anlatısı",
            en: "Sustainability narrative",
            de: "Nachhaltigkeitsnarrativ",
          },
          description:
            "The brand's sustainability/circularity story shown as a paragraph on the public page.",
          presentation: { kind: "prose" },
        },
        {
          id: "circularity.incentiveText",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Geri gönderim teşviki",
            en: "Return incentive",
            de: "Rückgabe-Anreiz",
          },
          description: "Step-by-step take-back instructions and any reward (e.g. a discount code).",
          presentation: { kind: "prose" },
        },
        {
          id: "circularity.awards",
          type: "group",
          required: false,
          tier: "public",
          labels: {
            tr: "Sürdürülebilirlik ödülleri",
            en: "Sustainability awards",
            de: "Nachhaltigkeitsauszeichnungen",
          },
          description: "Awards or recognitions earned for the product's ecological characteristics.",
          itemFields: [
            {
              key: "title",
              type: "string",
              required: true,
              labels: { tr: "Ödül adı", en: "Award title", de: "Auszeichnung" },
            },
            {
              key: "issuer",
              type: "string",
              required: false,
              labels: { tr: "Veren kuruluş", en: "Issuing organization", de: "Vergebende Organisation" },
            },
            {
              key: "year",
              type: "number",
              required: false,
              labels: { tr: "Yıl", en: "Year", de: "Jahr" },
            },
            {
              key: "url",
              type: "url",
              required: false,
              labels: { tr: "Bağlantı", en: "Link", de: "Link" },
            },
          ],
        },
      ],
    },
    {
      id: "use",
      labels: {
        tr: "Bakım, Kullanım ve Ömür Sonu",
        en: "Care, Use & End-of-Life",
        de: "Pflege, Gebrauch & Lebensende",
      },
      fields: [
        {
          id: "use.careInstructions",
          type: "string",
          required: true,
          tier: "public",
          labels: { tr: "Bakım talimatları", en: "Care instructions", de: "Pflegehinweise" },
          description: "ISO 3758 care symbol codes or free text.",
        },
        {
          id: "use.maxWashingTempC",
          type: "number",
          required: true,
          tier: "public",
          unit: "°C",
          labels: {
            tr: "Azami yıkama sıcaklığı",
            en: "Maximum washing temperature",
            de: "Maximale Waschtemperatur",
          },
          presentation: { kind: "careSymbol", symbol: "wash" },
        },
        {
          id: "use.bleachingAllowed",
          type: "boolean",
          required: false,
          tier: "public",
          labels: { tr: "Ağartma yapılabilir", en: "Bleaching allowed", de: "Bleichen erlaubt" },
          presentation: { kind: "careSymbol", symbol: "bleach" },
        },
        {
          id: "use.tumbleDryingAllowed",
          type: "boolean",
          required: false,
          tier: "public",
          labels: {
            tr: "Kurutma makinesi kullanılabilir",
            en: "Tumble drying allowed",
            de: "Trommeltrocknen erlaubt",
          },
          presentation: { kind: "careSymbol", symbol: "tumbleDry" },
        },
        {
          id: "use.naturalDrying",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["line_dry", "line_dry_shade", "flat_dry", "drip_dry"],
          labels: { tr: "Doğal kurutma", en: "Natural drying", de: "Natürliches Trocknen" },
          description: "Recommended non-tumble drying method (line dry, in shade, flat, drip).",
          presentation: { kind: "careSymbol", symbol: "naturalDry" },
        },
        {
          id: "use.ironingMaxLevel",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["none", "low", "medium", "high"],
          labels: { tr: "Azami ütüleme seviyesi", en: "Maximum ironing level", de: "Maximale Bügelstufe" },
          presentation: { kind: "careSymbol", symbol: "iron" },
        },
        {
          id: "use.dryCleaning",
          type: "enum",
          required: false,
          tier: "public",
          enumValues: ["none", "P", "F", "W"],
          labels: { tr: "Kuru temizleme", en: "Dry cleaning", de: "Chemische Reinigung" },
          description: "ISO 3758 professional care process letter; none = do not dry clean.",
          presentation: { kind: "careSymbol", symbol: "dryClean" },
        },
        {
          id: "use.eolCollectionInfo",
          type: "string",
          required: true,
          tier: "public",
          labels: {
            tr: "Ömür sonu toplama bilgisi",
            en: "End-of-life collection information",
            de: "Informationen zur Altkleidersammlung",
          },
          description:
            "Where/how to return the product — separate textile collection is EU-mandated since 2025.",
        },
        {
          id: "use.eolRecyclingGuidance",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Geri dönüşüm yönlendirmesi",
            en: "Recycling guidance",
            de: "Recyclinghinweise",
          },
        },
        {
          id: "use.takeBackProgramUrl",
          type: "url",
          required: false,
          tier: "public",
          labels: { tr: "Geri alma programı", en: "Take-back program", de: "Rücknahmeprogramm" },
        },
        {
          id: "use.reusePotential",
          type: "string",
          required: false,
          tier: "public",
          labels: {
            tr: "Yeniden kullanım potansiyeli",
            en: "Reuse potential",
            de: "Wiederverwendungspotenzial",
          },
          description: "Suitability for second-hand/donation channels.",
        },
      ],
    },
  ],
};
